import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import Nav from "./components/Nav";
import Footer from "./components/Footer";
import Container from "./components/Container";
import Stack from "./components/Stack";
import Button from "./components/Button";
import StarInput from "./components/StarInput";
import ReviewCard from "./components/ReviewCard";
import EmptyState from "./components/EmptyState";
import { getReviewId, getReviews, createReview } from "./api";
import styles from "./RateandComment.module.css";

const normalizeReview = (row) => ({
  text: row.review ?? row.description ?? "",
  when: row.when ?? "",
});

const isEmptyReviews = (rows) =>
  !Array.isArray(rows) || rows.length === 0 || rows[0].review === "No reviews yet!";

export default function RateandComment() {
  const history = useHistory();
  const providerName = localStorage.getItem("reviewname") || localStorage.getItem("rname") || "";
  const [rating, setRating] = useState(0);
  const [review, setReview] = useState("");
  const [reviews, setReviews] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const fetchProviderId = async () => {
    const res = await getReviewId(providerName);
    return res.data?.[0]?.user_id;
  };

  const loadReviews = async () => {
    try {
      const rid = await fetchProviderId();
      if (rid == null) return;
      const res = await getReviews(rid);
      setReviews(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.log(error);
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => {
    loadReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitReview = async (e) => {
    e.preventDefault();
    if (rating === 0) {
      toast.error("Please pick a rating.");
      return;
    }
    try {
      const rid = await fetchProviderId();
      if (String(rid) === String(localStorage.getItem("user_id"))) {
        toast.error("You cannot review yourself.");
        return;
      }
      await createReview({
        user_id: localStorage.getItem("user_id"),
        r_id: rid,
        rating,
        review,
        timestamp: Date.now(),
      });
      toast.success("Thanks for your review!");
      setRating(0);
      setReview("");
      loadReviews();
    } catch (error) {
      console.log(error);
      toast.error("Something went wrong. Try again.");
    }
  };

  const shown = isEmptyReviews(reviews) ? [] : reviews.map(normalizeReview);

  return (
    <div>
      <Nav />
      <main className={styles.page}>
        <Container>
          <h1 className={styles.title}>Rate {providerName || "this pro"}</h1>
          <div className={styles.grid}>
            <form className={styles.panel} onSubmit={submitReview}>
              <h2 className={styles.h2}>Leave a review</h2>
              <Stack gap="5">
                <div>
                  <span className={styles.label}>Your rating</span>
                  <StarInput value={rating} onChange={setRating} />
                </div>
                <div>
                  <label htmlFor="review" className={styles.label}>
                    Write a review
                  </label>
                  <textarea
                    id="review"
                    rows={5}
                    value={review}
                    onChange={(e) => setReview(e.target.value)}
                    style={{
                      width: "100%",
                      fontFamily: "var(--font-body)",
                      fontSize: "1rem",
                      padding: "var(--s3) var(--s4)",
                      background: "var(--plaster)",
                      border: "1.5px solid var(--line)",
                      borderRadius: "var(--r-input)",
                      color: "var(--ink)",
                      resize: "vertical",
                    }}
                  />
                </div>
                <Button type="submit">Submit review</Button>
              </Stack>
            </form>

            <div className={styles.panel}>
              <h2 className={styles.h2}>Reviews for {providerName || "this pro"}</h2>
              {shown.length > 0 ? (
                <div className={styles.reviews}>
                  {shown.map((r, i) => (
                    <ReviewCard key={i} when={r.when} text={r.text} />
                  ))}
                </div>
              ) : (
                loaded && (
                  <EmptyState title="No reviews yet" message="Be the first to leave feedback." />
                )
              )}
            </div>
          </div>
          <div style={{ marginTop: "var(--s6)" }}>
            <Button variant="ghost" onClick={() => history.goBack()}>
              ← Back
            </Button>
          </div>
        </Container>
      </main>
      <Footer />
    </div>
  );
}
